import { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { useAuth } from '@/components/auth/AuthContext';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';

interface ContactDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  defaultSubject?: string;
  title?: string;
  description?: string;
  context?: string;
}

export const ContactDialog = ({
  open,
  onOpenChange,
  defaultSubject = '',
  title = 'Contact Us',
  description = 'Send us a message and we will get back to you shortly.',
  context,
}: ContactDialogProps) => {
  const { user, isAnonymous } = useAuth();
  const { toast } = useToast();

  const [subject, setSubject] = useState(defaultSubject);
  const [message, setMessage] = useState('');
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (open) {
      setSubject(defaultSubject);
      setMessage('');
      if (user && !isAnonymous) {
        setEmail(user.email ?? '');
        setName(user.user_metadata?.name ?? user.user_metadata?.full_name ?? '');
      } else {
        setEmail('');
        setName('');
      }
    }
  }, [open, defaultSubject, user, isAnonymous]);

  const handleSubmit = async () => {
    if (!subject.trim() || !message.trim() || !email.trim()) {
      toast({ title: 'Missing fields', description: 'Subject, message and email are required.', variant: 'destructive' });
      return;
    }
    setSubmitting(true);
    const { data, error } = await supabase.functions.invoke('send-contact-message', {
      body: {
        subject: subject.trim(),
        message: message.trim(),
        email: email.trim(),
        name: name.trim() || undefined,
        context,
      },
    });
    setSubmitting(false);
    if (error || data?.error) {
      toast({
        title: 'Failed to send',
        description: error?.message || data?.error || 'Please try again later.',
        variant: 'destructive',
      });
      return;
    }
    toast({ title: 'Message sent', description: 'Thanks! We will be in touch shortly.' });
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div className="space-y-1.5">
            <Label htmlFor="contact-email">Your email</Label>
            <Input
              id="contact-email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@example.com"
              maxLength={255}
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="contact-subject">Subject</Label>
            <Input
              id="contact-subject"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              maxLength={200}
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="contact-message">Message</Label>
            <Textarea
              id="contact-message"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder="How can we help?"
              rows={6}
              maxLength={5000}
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={submitting}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={submitting}>
            {submitting ? 'Sending…' : 'Send message'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
